import React, { useEffect, useState } from "react";
import spinnerGif from "../../../assets/feedback-loading-spinner.gif";
import { LoadingItemTypes } from "../../../constant";
import { isFeedbackTimeoutTestingEnabled } from "../../../integrations/posthog/featureFlags";
import { usePostHogFeatureFlag } from "../../../integrations/posthog/usePostHogFeatureFlag";
import { FeedbackTimeoutModal } from "./FeedbackTimeoutModal";
import { api } from "../../../services/api";
import { ShowToast } from "../../../services/toast";

const TIMEOUT_MS = 2 * 60 * 1000;
const TESTING_TIMEOUT_MS = 2 * 1000;

export function CallProcessingLoadingState({
  sessionId,
  onNotifyAbort,
  onNotifySuccess,
}: {
  sessionId?: string;
  onNotifyAbort?: () => void;
  onNotifySuccess?: () => void;
}) {
  const [loadingItem, setLoadingItem] = useState<any>(null);

  useEffect(() => {
    api.getLoadingTips().then(res => {
      if (res.data && Object.keys(res.data).length > 0) {
        setLoadingItem(res.data);
      }
    });
  }, []);

  const timeoutTestingEnabled = usePostHogFeatureFlag(isFeedbackTimeoutTestingEnabled);

  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeFailed, setSubscribeFailed] = useState(false);

  useEffect(() => {
    if (!sessionId) return;
    const delay = timeoutTestingEnabled ? TESTING_TIMEOUT_MS : TIMEOUT_MS;
    const timer = setTimeout(() => setShowTimeoutModal(true), delay);
    return () => clearTimeout(timer);
  }, [sessionId, timeoutTestingEnabled]);

  const handleNotifyMe = async () => {
    if (!sessionId) return;
    setSubscribing(true);
    setSubscribeFailed(false);
    onNotifyAbort?.();
    try {
      await api.subscribeToReport(sessionId);
      ShowToast({ message: "We'll notify you when your feedback is ready", type: "success" });
      setSubscribing(false);
      onNotifySuccess?.();
    } catch {
      ShowToast({ message: "Couldn't set up your notification, but you can check back later for your feedback.", type: "error" });
      setSubscribing(false);
      setSubscribeFailed(true);
    }
  };

  const timeoutModal = showTimeoutModal && sessionId && (
    <FeedbackTimeoutModal
      onNotifyMe={handleNotifyMe}
      isLoading={subscribing}
      onSkipAnyway={subscribeFailed ? onNotifySuccess : undefined}
    />
  );

  return (
    <div
      className="fixed z-[4] inset-0 flex flex-col items-center justify-center"
      style={{ background: "linear-gradient(180deg, var(--menuPrimaryBg) 0%, var(--menuSecondaryBg) 100%)" }}
    >
      <h1 className="text-2xl font-semibold text-white">Generating Feedback</h1>
      <p className="mt-2 text-sm text-white/80">This usually takes around 30 seconds or less</p>

      <img src={spinnerGif} alt="" className="mt-8 w-[249px] h-[249px]" />

      {loadingItem && (
        <div className="mt-12 text-center max-w-[480px] px-4">
          {loadingItem.label && (
            <p className="text-lg font-semibold text-white">{loadingItem.label}</p>
          )}
          {loadingItem.body && (
            <p className="mt-0.5 text-sm text-white/80">
              {loadingItem.type === LoadingItemTypes.Testimonial ? `"${loadingItem.body}"` : loadingItem.body}
            </p>
          )}
          {loadingItem.signature && (
            <p className="mt-1 text-sm text-white/60 italic">{loadingItem.signature}</p>
          )}
        </div>
      )}

      {timeoutModal}
    </div>
  );
}
